/**
 * MySubscriptionInvoicesCard — iter 82.6.
 *
 * Muestra al tenant sus facturas de suscripción emitidas por ChefSync,
 * ordenadas por número desc. Permite descargar cada una en PDF.
 * Se renderiza dentro de /settings solo cuando el usuario es el owner del
 * tenant (no invitados / lectores).
 */
import { useCallback, useEffect, useState } from "react";
import { FileText, Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatDateShort } from "@/lib/format";

const fmtEur = (v) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" })
    .format(Number(v) || 0);

export default function MySubscriptionInvoicesCard({ lang = "es" }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/subscription-invoices");
      setInvoices(data.invoices || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const download = async (inv) => {
    try {
      const resp = await api.get(
        `/billing/subscription-invoices/${inv.id}/pdf`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(
        new Blob([resp.data], { type: "application/pdf" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(inv.number || "factura").replace("/", "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div
      className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-6 max-w-3xl"
      data-testid="my-subscription-invoices-card"
    >
      <div className="flex items-center gap-2 mb-4">
        <FileText size={18} className="text-[var(--primary)]" />
        <h2 className="font-display text-xl font-bold tracking-tight">
          {lang === "es" ? "Mis facturas de suscripción" : "My subscription invoices"}
        </h2>
      </div>
      {loading ? (
        <p className="text-sm text-[var(--text-muted)]">
          {lang === "es" ? "Cargando…" : "Loading…"}
        </p>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)]">
          {lang === "es"
            ? "Todavía no se han emitido facturas. Aparecerán aquí una vez el equipo de ChefSync las genere."
            : "No invoices issued yet. They'll show up here once the ChefSync team generates them."}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[var(--text-muted)] border-b border-[var(--border-color)] text-xs uppercase tracking-wider">
                <th className="py-2 pr-3">Nº</th>
                <th className="py-2 pr-3">{lang === "es" ? "Fecha" : "Date"}</th>
                <th className="py-2 pr-3">Plan</th>
                <th className="py-2 pr-3 text-right">Total</th>
                <th className="py-2 pr-3"> </th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr
                  key={inv.id}
                  className="border-b border-[var(--border-color)] last:border-b-0"
                  data-testid={`my-invoice-row-${inv.id}`}
                >
                  <td className="py-2 pr-3 font-mono-num text-[var(--text-secondary)]">
                    {inv.number}
                  </td>
                  <td className="py-2 pr-3">{formatDateShort(inv.issue_date)}</td>
                  <td className="py-2 pr-3">
                    <span className="inline-block px-1.5 py-0.5 rounded bg-[var(--surface-hover)] text-[10px] uppercase font-semibold">
                      {inv.plan}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-right font-mono-num font-semibold">
                    {fmtEur(inv.total)}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    <button
                      type="button"
                      onClick={() => download(inv)}
                      className="text-[var(--primary)] hover:underline text-xs inline-flex items-center gap-1"
                      data-testid={`my-invoice-pdf-${inv.id}`}
                    >
                      <Download size={12} /> PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
